import { useRef, useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import * as Selectors from '~/selectors'
import { PlaceholderAsset, TransitionAsset } from '~/models/Asset'
import { DraggingItemContext } from '~/components/Timeline/DraggingItemProvider'
import { time2Pixel } from '~/Util'
import { useMultipleDragRectCoords } from './useMultipleDragRectCoords'
import { snapToLayer } from './snapToLayer'
import { getPlaceholdersByLayerIndex } from './getPlaceholdersByLayerIndex'
import { getStaticAssetsByLayerIndex } from './getStaticAssetsByLayerIndex'
import { getStartNTopDraggableLayers } from './getStartNTopDraggableLayers'


export const useMultipleDragging = ({
  dragItem,
  clientOffset,
  initialClientOffset,
  scale,
  overNewLayerIndex,
  scrollTop,
  targetLayerIndex,
  sourceOffset,
  timelineTopOffset,
  timelineLeftOffset,
  scrollLeft,
  isMultipleDrag,
  sliderTimeX,
  dragAssetsWidth,
  setSliderSticky,
  timelineMouseDown,
  isExternalSource,
  multipleDragAssets,
  draggableBlockDuration,
}) => {
  /* eslint-disable react-hooks/rules-of-hooks */
  if (!isMultipleDrag) return {}

  const { setProps: setDraggingItemProps } = useContext(DraggingItemContext)

  const transitions = useSelector(state => Selectors.getTransitionAssets(state))
  const leftXOffset = timelineLeftOffset - scrollLeft

  // History of movement of dragged assets on the timeline
  const historyX = useRef({
    isMountedOnLayer: false,
    dropX: 0,
    draggableBlockLeftX: 0,
    stopDragLeftX: 0,
    readyForDrop: false,
    isIntersected: false,
    intersectedData: null,
    moveDirection: null,
    currentLayerIndex: null,
    isIncorrectPosition: false,
  })

  const allAssets = useSelector(Selectors.getAssets)
  const staticAssets = useMemo(() => allAssets.filter(a => !(a instanceof PlaceholderAsset)
    && !(a instanceof TransitionAsset)
    && !dragItem.selectedDragIds?.includes(a.id)), [ dragItem.selectedDragIds, allAssets ])

  const draggableLayers = getStartNTopDraggableLayers(dragItem)
  // Offset of the currently dragged asset relative to the top asset
  const diffBetweenCurNTopDragLayers = isExternalSource
    ? 0
    : draggableLayers.startLayer.index - draggableLayers.topLayer.index
  const layers = useSelector(Selectors.getLayers)
  const layerIds = useMemo(() => layers.map(layer => layer.id), [ layers ])

  const staticAssetsByLayerIndex = useMemo(() => getStaticAssetsByLayerIndex(
    staticAssets, layerIds
  ), [ layerIds, staticAssets ])
  const placeholdersByLayerIndex = useMemo(() => getPlaceholdersByLayerIndex(
    staticAssetsByLayerIndex, layerIds
  ), [ layerIds, staticAssetsByLayerIndex ])

  const {
    draggableBlockLeftX,
    draggableBlockY,
    intersectionPlaceholder,
    isIncorrectPosition,
  } = useMultipleDragRectCoords({
    dragItem,
    clientOffset,
    initialClientOffset,
    sourceOffset,
    leftXOffset,
    timelineTopOffset,
    setDraggingItemProps,
    historyX,
    staticAssets,
    scale,
    draggableAssets: isExternalSource ? multipleDragAssets : dragItem.selectedAssets ?? [],
    overNewLayerIndex,
    layers,
    layerIds,
    targetLayerIndex,
    placeholdersByLayerIndex,
    diffBetweenCurNTopDragLayers,
    isMultipleDrag,
    transitions,
    sliderTimeX,
    dragAssetsWidth,
    setSliderSticky,
    timelineMouseDown,
    isExternalSource,
    draggableBlockDuration,
  })

  // Coords and wodth for preview for placeholder paste
  const multipleSnappedPreview = {
    x: time2Pixel(intersectionPlaceholder?.startTime ?? 0, scale) + leftXOffset,
    width: time2Pixel(intersectionPlaceholder?.duration ?? 0, scale),
    y: snapToLayer({
      layerIndex: targetLayerIndex,
      timelineScrollTop: scrollTop,
    }),
  }

  return {
    draggableBlockLeftX,
    draggableBlockY: overNewLayerIndex === null && !isIncorrectPosition
      ? snapToLayer({
        layerIndex: targetLayerIndex - diffBetweenCurNTopDragLayers,
        timelineScrollTop: scrollTop,
      })
      : draggableBlockY,
    multipleDragAssets,
    transitions,
    multipleSnappedPreview,
  }
}
